
// WHY: атлас — один HTMLImageElement, в canvas не резать: iOS WebKit молча чистит удержанные canvas, спрайты пропадают
import {
  loadKeyedSprite, type Sprite,
  allBodySources, splitBodySources, WEAPON_SRC, weaponSrcFor, allWeaponSkinSources, WEAPON_ICON_SRC, GRENADE_SRC, SMOKE_NADE_SRC, FLASH_NADE_SRC, KNIFE_SRC,
  GRENADE_ICON_SRC, SMOKE_ICON_SRC, MEDKIT_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, KNIFE_KILL_ICON_SRC, HEADSHOT_ICON_SRC,
} from '../spriteLayout';
import { loadFrames, loadPlain, sliceRunSheet } from '../render/loadAssets';
import { ATLAS_INDEX, ATLAS_PAGES } from './atlasManifest';
import { bustCache } from '../render/battleAssetVersion';
import { getAsset, getAssetImage, pruneAssets } from '../../../lib/assetStore';
import { soundUrl, UI_SOUND_NAMES } from '../../../lib/audioEngine';
import { SFX } from './sfx';

const RUN_SUFFIX = '_legs_run';
import type { Frame } from '../render/types';
import { type RushMidMap, type Team, RUSH_MID_MAPS } from '@cs-shot/battle-sim';

const EXPLOSION = '/battle/explosion.webp';
const TEAM_ICON: Record<Team, string> = { t: '/battle/t.webp', ct: '/battle/ct.webp' };
const DEFAULT_MAP: RushMidMap = 'dust2';
const bgSrc = (map: RushMidMap) => `/battle/bg_${map}.webp`;
const coverSrc = (map: RushMidMap) => `/battle/cover_${map}.webp`;
const LOADING_ART = '/battle/rush_mid_loading.webp';

function battleAssetKeepSet(): string[] {
  return [
    ATLAS_INDEX,
    ...ATLAS_PAGES,
    bustCache(TEAM_ICON.t), bustCache(TEAM_ICON.ct),
    bustCache(EXPLOSION),
    bustCache(LOADING_ART),
    ...RUSH_MID_MAPS.map((m) => bustCache(bgSrc(m))),
    ...RUSH_MID_MAPS.map((m) => bustCache(coverSrc(m))),
    ...[...SFX, 'menu_theme', ...UI_SOUND_NAMES].map(soundUrl),
  ];
}

export interface BattleAssets {
  bg: HTMLImageElement;
  explosionImg: ImageBitmap | HTMLImageElement;
  explosionFrames: Frame[];
  teamIcons: Record<Team, HTMLImageElement>;
  bodies: Record<string, Sprite>;
  runFrames: Record<string, Sprite[]>;
  weapons: Record<string, Sprite>;
  weaponSkins: Record<string, Sprite>;
  nade: Sprite;
  smokeNade: Sprite;
  flashNade: Sprite;
  knife: Sprite;
  utilIcons: Record<'grenade' | 'smoke' | 'medkit' | 'flash' | 'knife', Sprite>;
  weaponIcons: Record<string, Sprite>;
  headshotIcon: Sprite;
  slash: Sprite;
  smokeCloud: Sprite;
  casing: Sprite;
}

type SharedAssets = Omit<BattleAssets, 'bg'> & {
  bgs: Record<RushMidMap, HTMLImageElement>;
  covers: Record<RushMidMap, HTMLImageElement>;
};

const BLOOD = ['blood1', 'blood2', 'blood3', 'blood4', 'blood5', 'blood6'];

const NON_ATLAS_FILES = RUSH_MID_MAPS.length * 2 + 1 + 2;
const ATLAS_PROGRESS_UNITS = 100;

export function battleAssetCount(): number {
  return ATLAS_PROGRESS_UNITS + NON_ATLAS_FILES;
}

interface AtlasBox { page: number; x: number; y: number; w: number; h: number; bbox: [number, number, number, number]; }
interface AtlasFrames { frames: AtlasBox[]; }
interface AtlasIndex {
  hash: string;
  pages: { file: string; w: number; h: number; bytes: number }[];
  sprites: Record<string, AtlasBox | AtlasFrames>;
}

const atlasKey = (src: string): string => src.split('?')[0].split('/').pop() ?? src;
const isFrames = (e: AtlasBox | AtlasFrames): e is AtlasFrames => 'frames' in e;
const spriteFromBox = (pages: HTMLImageElement[], e: AtlasBox): Sprite => ({
  canvas: pages[e.page], sx: e.x, sy: e.y, sw: e.w, sh: e.h,
  bx: e.bbox[0], by: e.bbox[1], bw: e.bbox[2], bh: e.bbox[3],
});

async function loadAtlas(onByte: (frac: number) => void): Promise<{ idx: AtlasIndex; pages: HTMLImageElement[] } | null> {
  try {
    const idx: AtlasIndex = await getAsset(ATLAS_INDEX).then((b) => b.text()).then((t) => JSON.parse(t));
    const totalBytes = idx.pages.reduce((a, p) => a + p.bytes, 0) || 1;
    // WHY: страницы атласа через IDB-стор — iOS TMA webview игнорирует HTTP-кэш и перекачивает их каждую сессию
    let loaded = 0;
    const onChunk = (n: number) => { loaded += n; onByte(loaded / totalBytes); };
    const pages = await Promise.all(idx.pages.map((p) => getAssetImage(p.file, onChunk)));
    return { idx, pages };
  } catch {
    return null;
  }
}

let sharedCache: Promise<SharedAssets> | null = null;
let ready = false;

export function isBattleAssetsReady(): boolean {
  return ready;
}

// WHY: per-file fallback без пула декодирует ~130 спрайтов разом — пик памяти OOM-килл WebView на слабых Android
async function runPool(tasks: Array<() => Promise<void>>, limit: number): Promise<void> {
  let next = 0;
  const worker = async () => { while (next < tasks.length) await tasks[next++](); };
  await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
}

const SPRITE_LOAD_CONCURRENCY = 6;

function loadShared(onUnit?: () => void): Promise<SharedAssets> {
  if (sharedCache) return sharedCache;
  sharedCache = (async () => {
    void pruneAssets(battleAssetKeepSet());
    const tick = () => { try { onUnit?.(); } catch { } };
    const bodies: Record<string, Sprite> = {};
    const runFrames: Record<string, Sprite[]> = {};
    const weapons: Record<string, Sprite> = {};
    const weaponSkins: Record<string, Sprite> = {};
    const weaponIcons: Record<string, Sprite> = {};
    const utilIcons: Record<string, Sprite> = {};
    const bgs: Record<string, HTMLImageElement> = {};
    const covers: Record<string, HTMLImageElement> = {};

    let atlasTicks = 0;
    const onByte = (frac: number) => {
      const want = Math.min(ATLAS_PROGRESS_UNITS, Math.floor(frac * ATLAS_PROGRESS_UNITS));
      while (atlasTicks < want) { atlasTicks++; tick(); }
    };
    const atlas = await loadAtlas(onByte);
    while (atlasTicks < ATLAS_PROGRESS_UNITS) { atlasTicks++; tick(); }

    const atlasGet = (src: string): Sprite | null => {
      if (!atlas) return null;
      const e = atlas.idx.sprites[atlasKey(src)];
      return e && !isFrames(e) ? spriteFromBox(atlas.pages, e) : null;
    };
    const atlasFrames = (src: string): Sprite[] | null => {
      if (!atlas) return null;
      const e = atlas.idx.sprites[atlasKey(src)];
      return e && isFrames(e) ? e.frames.map((fr) => spriteFromBox(atlas.pages, fr)) : null;
    };

    const fallbacks: Array<() => Promise<void>> = [];
    const putSprite = (target: Record<string, Sprite>, key: string, src: string) => {
      const sp = atlasGet(src);
      if (sp) { target[key] = sp; return; }
      fallbacks.push(() => loadKeyedSprite(src).then((s) => { target[key] = s; }).catch(() => { }));
    };

    for (const k of BLOOD) putSprite(bodies, k, `/battle/${k}.webp`);
    for (const { key, src } of allBodySources()) putSprite(bodies, key, src);
    for (const { key, src } of splitBodySources()) {
      if (key.endsWith(RUN_SUFFIX)) {
        const prefix = key.slice(0, -RUN_SUFFIX.length);
        const fr = atlasFrames(src);
        if (fr) runFrames[prefix] = fr;
        else fallbacks.push(() => loadKeyedSprite(src).then((s) => sliceRunSheet(s, 4)).then((f) => { runFrames[prefix] = f; }).catch(() => { }));
      } else {
        putSprite(bodies, key, src);
      }
    }
    for (const k of Object.keys(WEAPON_SRC)) putSprite(weapons, k, weaponSrcFor(k, 0));
    for (const { name, src } of allWeaponSkinSources()) putSprite(weaponSkins, name, src);
    for (const [k, src] of Object.entries(WEAPON_ICON_SRC)) putSprite(weaponIcons, k, src);
    putSprite(weaponIcons, 'knife', KNIFE_KILL_ICON_SRC);
    putSprite(utilIcons, 'grenade', GRENADE_ICON_SRC);
    putSprite(utilIcons, 'smoke', SMOKE_ICON_SRC);
    putSprite(utilIcons, 'medkit', MEDKIT_ICON_SRC);
    putSprite(utilIcons, 'flash', FLASH_ICON_SRC);
    putSprite(utilIcons, 'knife', KNIFE_ICON_SRC);

    const single = (src: string): Promise<Sprite> => {
      const sp = atlasGet(src);
      return sp ? Promise.resolve(sp) : loadKeyedSprite(src);
    };
    const [nade, smokeNade, flashNade, knife, headshotIcon, slash, smokeCloud, casing] = await Promise.all([
      single(GRENADE_SRC), single(SMOKE_NADE_SRC), single(FLASH_NADE_SRC), single(KNIFE_SRC),
      single(HEADSHOT_ICON_SRC), single('/battle/slash.webp'), single('/battle/smoke.webp'), single('/battle/casing.webp'),
    ]);

    let explosion!: Awaited<ReturnType<typeof loadFrames>>;
    let tIcon!: HTMLImageElement; let ctIcon!: HTMLImageElement;
    await runPool([
      ...fallbacks,
      () => loadFrames(EXPLOSION).then((r) => { explosion = r; tick(); }),
      () => loadPlain(TEAM_ICON.t).then((r) => { tIcon = r; tick(); }),
      () => loadPlain(TEAM_ICON.ct).then((r) => { ctIcon = r; tick(); }),
      ...RUSH_MID_MAPS.map((m) => () => loadPlain(bgSrc(m)).then((img) => { bgs[m] = img; tick(); })),
      ...RUSH_MID_MAPS.map((m) => () => loadPlain(coverSrc(m)).then((img) => { covers[m] = img; tick(); })),
    ], SPRITE_LOAD_CONCURRENCY);

    weaponIcons.grenade = utilIcons.grenade;
    ready = true;
    return {
      explosionImg: explosion.img, explosionFrames: explosion.frames,
      teamIcons: { t: tIcon, ct: ctIcon }, bodies, runFrames, weapons, weaponSkins, weaponIcons, headshotIcon, nade, smokeNade, flashNade, knife, slash, smokeCloud, casing, utilIcons, bgs, covers,
    };
  })();
  return sharedCache;
}

export function loadBattleAssets(map: RushMidMap = DEFAULT_MAP, onUnit?: () => void): Promise<BattleAssets> {
  return loadShared(onUnit).then((shared) => {
    const { bgs, covers: _covers, ...rest } = shared;
    return { bg: bgs[map], ...rest };
  });
}
